import { useHouseholds } from '@platewell/query';
import { MAX_HOUSEHOLD_MEMBERS } from '@platewell/schema';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { inviteMember, listMembers, listPendingInvites, revokeInvite } from '../../lib/api.js';

export interface Member {
  userId: string;
  name: string;
  email: string;
  role: string;
}

export interface PendingInvite {
  id: string;
  email: string;
  role: string;
  expiresAt: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Rounded up, so an invite with hours left reads as a day rather than as already gone. */
export function expiresIn(expiresAt: number, now = Date.now()): string {
  const days = Math.max(1, Math.ceil((expiresAt - now) / DAY_MS));
  return days === 1 ? 'Expires in 1 day' : `Expires in ${days} days`;
}

/** Two words at most, so a long name does not turn into a crowded circle. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts
    .slice(0, 2)
    .map((word) => (word[0] ?? '').toUpperCase())
    .join('');
}

export function HouseholdTab() {
  const { active, activeHouseholdId } = useHouseholds();
  const navigate = useNavigate();
  const [members, setMembers] = useState<Member[]>([]);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'editor' | 'viewer' | 'owner'>('editor');
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [invitedAddress, setInvitedAddress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => {
    if (activeHouseholdId == null) return;
    let cancelled = false;
    void listMembers(activeHouseholdId)
      .then((result) => {
        if (!cancelled) setMembers(result.members);
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [activeHouseholdId, inviteUrl]);

  // Owners only: the server refuses anyone else, and the invite form is theirs alone too.
  const isOwner = active?.role === 'owner';
  // Pending invites hold a seat too. Only owners see the invite button, and they always have
  // the pending list loaded, so this matches the server's count.
  const full = members.length + invites.length >= MAX_HOUSEHOLD_MEMBERS;
  // The free plan is for one person, so the invite form gives way to the way out of it.
  const needsSubscription = active?.plan === 'free';
  useEffect(() => {
    if (activeHouseholdId == null || !isOwner) {
      setInvites([]);
      return;
    }
    let cancelled = false;
    void listPendingInvites(activeHouseholdId)
      .then((result) => {
        if (!cancelled) setInvites(result.invites);
      })
      .catch(() => {
        if (!cancelled) setInvites([]);
      });
    return () => {
      cancelled = true;
    };
  }, [activeHouseholdId, isOwner, inviteUrl]);

  async function removeInvite(inviteId: string) {
    if (activeHouseholdId == null) return;
    setRemovingId(inviteId);
    setError(null);
    try {
      await revokeInvite(activeHouseholdId, inviteId);
      setInvites((current) => current.filter((pending) => pending.id !== inviteId));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not remove the invite');
    } finally {
      setRemovingId(null);
    }
  }

  function closeForm() {
    setFormOpen(false);
    setEmail('');
    setError(null);
  }

  async function invite() {
    if (activeHouseholdId == null || sending) return;
    setSending(true);
    setError(null);
    try {
      const address = email.trim();
      const { url } = await inviteMember(activeHouseholdId, address, role);
      setInviteUrl(new URL(url, window.location.origin).toString());
      setInvitedAddress(address);
      setEmail('');
      setFormOpen(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not send the invite');
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="section-group">
      <h2 className="section-title">People</h2>
      <p className="hint">
        Everyone here shares the same recipes, collections and schedule. Favourited recipes are tied to you.
      </p>

      <div>
        {members.map((member) => (
          <div className="person-row" key={member.userId}>
            <span className="person-avatar" aria-hidden="true">
              {initials(member.name || member.email)}
            </span>
            <span className="person-identity">
              <span className="person-name">{member.name || member.email}</span>
              <span className="person-sub">{member.email}</span>
            </span>
            <span className="spacer" />
            <span className={member.role === 'owner' ? 'pill pill--owner' : 'pill'}>
              {member.role}
            </span>
          </div>
        ))}
        {members.length === 0 && <p className="hint">Just you, for now.</p>}
      </div>

      {invites.length > 0 && (
        <div>
          <span className="label">Invited</span>
          {invites.map((pending) => (
            <div className="person-row" key={pending.id}>
              <span className="person-avatar" aria-hidden="true">
                {initials(pending.email)}
              </span>
              <span className="person-identity">
                <span className="person-name">{pending.email}</span>
                <span className="person-sub">{expiresIn(pending.expiresAt)}</span>
              </span>
              <span className="spacer" />
              <span className="pill">{pending.role}</span>
              <button
                type="button"
                className="btn small"
                disabled={removingId != null}
                aria-label={`Remove invite for ${pending.email}`}
                onClick={() => void removeInvite(pending.id)}
              >
                {removingId === pending.id ? 'Removing…' : 'Remove'}
              </button>
            </div>
          ))}
        </div>
      )}

      {isOwner && needsSubscription && (
        <div className="upgrade-notice" role="status">
          <div className="upgrade-notice-text">
            <p className="upgrade-notice-headline">Adding people needs a subscription.</p>
            <p className="hint">
              The free plan is for one person. Subscribe to invite your family, and everyone in
              the household is covered.
            </p>
          </div>
          <button
            type="button"
            className="btn primary"
            onClick={() => void navigate({ to: '/subscribe' })}
          >
            Subscribe
          </button>
        </div>
      )}

      {isOwner && !needsSubscription && (
        <>
          {!formOpen && (
            <div className="row" style={{ marginTop: 6 }}>
              <button
                type="button"
                className="btn"
                disabled={full}
                onClick={() => setFormOpen(true)}
              >
                Add family member
              </button>
            </div>
          )}
          {!formOpen && full && (
            <p className="hint">
              Households can have up to {MAX_HOUSEHOLD_MEMBERS} people, including pending invites.
              Remove an invite to make room.
            </p>
          )}

          {formOpen && (
            <form
              className="row wrap"
              style={{ marginTop: 6 }}
              onSubmit={(event) => {
                event.preventDefault();
                void invite();
              }}
            >
              <input
                className="input"
                style={{ maxWidth: 260 }}
                type="email"
                value={email}
                disabled={sending}
                autoFocus
                aria-label="Email address"
                placeholder="friend@example.com"
                onChange={(event) => setEmail(event.target.value)}
              />
              <select
                className="select"
                style={{ maxWidth: 140 }}
                value={role}
                disabled={sending}
                onChange={(event) => setRole(event.target.value as typeof role)}
              >
                <option value="editor">Can edit</option>
                <option value="viewer">Can view</option>
                <option value="owner">Owner</option>
              </select>
              <button
                type="submit"
                className="btn primary"
                disabled={sending || email.trim() === ''}
              >
                {sending ? 'Sending…' : 'Send invite'}
              </button>
              <button type="button" className="btn ghost" disabled={sending} onClick={closeForm}>
                Cancel
              </button>
            </form>
          )}

          {inviteUrl != null && (
            <p className="hint">
              {/* The email is sent fire-and-forget and can fail silently, so the link stays
                  on screen as the fallback that always works. */}
              Invite sent to {invitedAddress}. You can also pass along this link directly:{' '}
              <code>{inviteUrl}</code>
            </p>
          )}
        </>
      )}

      {error != null && <p className="error-text">{error}</p>}
    </section>
  );
}
